import { forwardRef } from "react";
import styled from "styled-components";
import { Mail } from "lucide-react";

const Wrapper = styled.div`
  width: 100%;
  min-width: 0;
  position: relative;

  .poda-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .poda {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .glow,
  .white,
  .border,
  .darkBorderBg {
    position: absolute;
    inset: -4px;
    border-radius: 12px;
    overflow: hidden;
    pointer-events: none;
    z-index: 0;
  }

  .glow {
    filter: blur(24px);
    opacity: 0.35;
  }

  .white,
  .border {
    filter: blur(2px);
  }

  .border {
    filter: blur(0.5px);
    inset: -2px;
    border-radius: 11px;
  }

  .darkBorderBg {
    inset: -3px;
    filter: blur(3px);
  }

  .glow::before,
  .white::before,
  .border::before,
  .darkBorderBg::before {
    content: "";
    z-index: -2;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 600px;
    height: 600px;
    background-repeat: no-repeat;
    background-position: 0 0;
    transform: translate(-50%, -50%) rotate(var(--r, 0deg));
    transition: transform 2s ease;
  }

  .white::before {
    --r: 83deg;
    filter: brightness(1.35);
    background-image: conic-gradient(
      rgba(0, 0, 0, 0) 0%,
      #a099d8,
      rgba(0, 0, 0, 0) 8%,
      rgba(0, 0, 0, 0) 50%,
      #dfa2da,
      rgba(0, 0, 0, 0) 58%
    );
  }

  .border::before {
    --r: 70deg;
    filter: brightness(1.25);
    background-image: conic-gradient(
      #1c191c,
      #6d28d9 5%,
      #1c191c 14%,
      #1c191c 50%,
      #a855f7 60%,
      #1c191c 64%
    );
  }

  .darkBorderBg::before {
    --r: 82deg;
    background-image: conic-gradient(
      rgba(0, 0, 0, 0),
      #312e81,
      rgba(0, 0, 0, 0) 10%,
      rgba(0, 0, 0, 0) 50%,
      #6b21a8,
      rgba(0, 0, 0, 0) 60%
    );
  }

  .glow::before {
    --r: 60deg;
    width: 900px;
    height: 900px;
    background-image: conic-gradient(
      #000,
      #5b21b6 5%,
      #000 38%,
      #000 50%,
      #a855f7 60%,
      #000 87%
    );
  }

  .poda:hover .darkBorderBg::before {
    transform: translate(-50%, -50%) rotate(262deg);
  }
  .poda:hover .glow::before {
    transform: translate(-50%, -50%) rotate(240deg);
  }
  .poda:hover .white::before {
    transform: translate(-50%, -50%) rotate(263deg);
  }
  .poda:hover .border::before {
    transform: translate(-50%, -50%) rotate(250deg);
  }

  .poda:focus-within .darkBorderBg::before {
    transform: translate(-50%, -50%) rotate(442deg);
    transition: transform 3.5s ease;
  }
  .poda:focus-within .glow::before {
    transform: translate(-50%, -50%) rotate(420deg);
    transition: transform 3.5s ease;
  }
  .poda:focus-within .white::before {
    transform: translate(-50%, -50%) rotate(443deg);
    transition: transform 3.5s ease;
  }
  .poda:focus-within .border::before {
    transform: translate(-50%, -50%) rotate(430deg);
    transition: transform 3.5s ease;
  }

  @media (prefers-reduced-motion: reduce) {
    .glow::before,
    .white::before,
    .border::before,
    .darkBorderBg::before {
      transition: none;
    }
    .poda:hover .darkBorderBg::before,
    .poda:hover .glow::before,
    .poda:hover .white::before,
    .poda:hover .border::before,
    .poda:focus-within .darkBorderBg::before,
    .poda:focus-within .glow::before,
    .poda:focus-within .white::before,
    .poda:focus-within .border::before {
      transform: translate(-50%, -50%) rotate(90deg);
    }
  }

  .main {
    position: relative;
    z-index: 1;
    width: 100%;
    border-radius: 10px;
    background: #08080a;
    overflow: hidden;
    isolation: isolate;
  }

  .input {
    box-sizing: border-box;
    width: 100%;
    height: 52px;
    padding-inline: 3rem 1rem;
    padding-block: 0;
    border: none;
    border-radius: 10px;
    background-color: #08080a;
    color: #f4f4f5;
    font-size: 1rem;
    line-height: 1.25;
    color-scheme: dark;
    -webkit-appearance: none;
    appearance: none;
  }

  .input::placeholder {
    color: rgba(255, 255, 255, 0.72);
    opacity: 1;
  }

  .input:focus {
    outline: none;
    background-color: #08080a;
    color: #fafafa;
  }

  .input:-webkit-autofill,
  .input:-webkit-autofill:hover,
  .input:-webkit-autofill:focus,
  .input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px #08080a inset;
    box-shadow: 0 0 0 1000px #08080a inset;
    -webkit-text-fill-color: #f4f4f5;
    caret-color: #f4f4f5;
    transition: background-color 99999s ease-out 0s;
  }

  .input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .pink-mask {
    pointer-events: none;
    width: 28px;
    height: 18px;
    position: absolute;
    background: #a855f7;
    top: 8px;
    left: 6px;
    filter: blur(18px);
    opacity: 0.55;
    transition: opacity 1.5s ease;
  }

  .main:hover .pink-mask,
  .main:focus-within .pink-mask {
    opacity: 0;
  }

  /* Keep border glow; stop light conic layer from washing the field when focused */
  .poda:focus-within .white {
    opacity: 0.35;
  }

  .mail-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    color: #c4b5fd;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .mail-icon svg {
    width: 1.25rem;
    height: 1.25rem;
    stroke-width: 2;
  }
`;

const GlowingEmailInput = forwardRef(function GlowingEmailInput(
  {
    value,
    onChange,
    placeholder = "you@company.com",
    name = "email",
    id,
    disabled,
    className,
    "data-testid": dataTestId,
    "aria-label": ariaLabel,
    autoComplete = "email",
    inputMode = "email",
  },
  ref
) {
  return (
    <Wrapper className={className}>
      <div className="poda-wrap">
        <div className="poda">
          <div className="glow" aria-hidden />
          <div className="darkBorderBg" aria-hidden />
          <div className="darkBorderBg" aria-hidden />
          <div className="darkBorderBg" aria-hidden />
          <div className="white" aria-hidden />
          <div className="border" aria-hidden />
          <div className="main">
            <span className="mail-icon" aria-hidden>
              <Mail />
            </span>
            <input
              ref={ref}
              id={id}
              name={name}
              type="email"
              className="input"
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              disabled={disabled}
              data-testid={dataTestId}
              aria-label={ariaLabel}
              autoComplete={autoComplete}
              inputMode={inputMode}
            />
            <div className="pink-mask" aria-hidden />
          </div>
        </div>
      </div>
    </Wrapper>
  );
});

export default GlowingEmailInput;
